import { Heart, User, Check } from 'lucide-react';
import { Textarea } from '~/components/ui/textarea';
import { Label } from '~/components/ui/label';
import { Input } from '~/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { Separator } from '~/components/ui/separator';
import { OtcMedicationForm } from '~/components/forms/OtcMedicationForm';
import { useTabReviewTracker } from '~/hooks/useTabReviewTracker';

interface StudentInfo {
  id: string;
  firstName: string;
  lastName: string;
  type: 'returning' | 'new';
}

interface StudentMedicalChunkProps {
  students: StudentInfo[];
  medicalInfo: Record<string, any>;
  onUpdate: (medicalInfo: Record<string, any>) => void;
  onReviewComplete?: (reviewed: boolean) => void;
  showOtcMedications?: boolean;
}

/**
 * Reusable student medical info chunk.
 * Used by both reenrollment StudentMedicalStep and application AppStudentMedicalStep.
 */
export function StudentMedicalChunk({
  students,
  medicalInfo,
  onUpdate,
  onReviewComplete,
  showOtcMedications = true,
}: StudentMedicalChunkProps) {
  const studentIds = students.map(s => s.id);
  const { activeId, setActiveId, visitedIds, allVisited } = useTabReviewTracker(studentIds, onReviewComplete);

  const updateMedicalInfo = (studentId: string, field: string, value: any) => {
    const currentInfo = medicalInfo[studentId] || {};
    onUpdate({
      ...medicalInfo,
      [studentId]: {
        ...currentInfo,
        [field]: value,
      },
    });
  };

  if (students.length === 0) {
    return (
      <Card className="max-w-xl mx-auto">
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            No students selected for enrollment. Please go back and select at least one student.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {students.length === 1 ? (
        <MedicalForm
          student={students[0]}
          info={medicalInfo[students[0].id] || {}}
          onChange={(field: string, value: any) => updateMedicalInfo(students[0].id, field, value)}
          showOtcMedications={showOtcMedications}
        />
      ) : (
        <>
          {!allVisited && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Please review medical information for each student before continuing.
            </div>
          )}
          <Tabs value={activeId} onValueChange={setActiveId}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              {students.map((student) => (
                <TabsTrigger
                  key={student.id}
                  value={student.id}
                  className="flex items-center gap-2"
                >
                  <User className="h-4 w-4" />
                  {student.firstName}
                  {student.type === 'new' && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded">new</span>
                  )}
                  {visitedIds.has(student.id) && (
                    <Check className="h-3 w-3 text-green-600" />
                  )}
                </TabsTrigger>
              ))}
            </TabsList>

            {students.map((student) => (
              <TabsContent key={student.id} value={student.id} className="mt-6">
                <MedicalForm
                  student={student}
                  info={medicalInfo[student.id] || {}}
                  onChange={(field: string, value: any) => updateMedicalInfo(student.id, field, value)}
                  showOtcMedications={showOtcMedications}
                />
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}
    </div>
  );
}

/**
 * One student's medical questions. `fields` limits it to the questions a
 * school asks, by the profile's field names (medicalCondition,
 * otcMedicationPermission and otcMedicationsApproved, specialNeeds,
 * insuranceInfo); left out, it asks them all.
 */
export function MedicalForm({ student, info, onChange, showOtcMedications = true, fields }: {
  student: StudentInfo;
  info: any;
  onChange: (field: string, value: any) => void;
  showOtcMedications?: boolean;
  fields?: string[];
}) {
  const asks = (...names: string[]) => !fields || names.some((name) => fields.includes(name));
  const showCondition = asks('medicalCondition');
  const showOtc = showOtcMedications && asks('otcMedicationPermission', 'otcMedicationsApproved');
  const showSpecial = asks('specialNeeds');
  const showInsurance = asks('insuranceInfo');
  const medications = info.otcMedicationsApproved || {
    acetaminophen: false,
    ibuprofen: false,
    coughDrops: false,
    other: null
  };
  const insuranceInfo = info.insuranceInfo || {};

  const handleOtcPermissionChange = (hasPermission: boolean) => {
    onChange('otcMedicationPermission', hasPermission);
    if (!hasPermission) {
      onChange('otcMedicationsApproved', {
        acetaminophen: false,
        ibuprofen: false,
        coughDrops: false,
        other: null
      });
    }
  };

  const handleMedicationToggle = (medication: string, checked: boolean) => {
    onChange('otcMedicationsApproved', {
      ...medications,
      [medication]: checked
    });
  };

  const handleOtherMedicationChange = (value: string) => {
    onChange('otcMedicationsApproved', {
      ...medications,
      other: value || null
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Heart className="h-5 w-5 text-red-500" />
          {[student.firstName, student.lastName].filter(Boolean).join(' ')}'s Medical Information
        </CardTitle>
        <CardDescription>
          {student.type === 'new'
            ? "Please provide medical information for this new student."
            : "Please review and update the medical information on file."
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Medical Condition */}
        {showCondition && (
        <div className="space-y-2">
          <Label htmlFor={`condition-${student.id}`}>
            Does your child have a medical condition?
          </Label>
          <Textarea
            id={`condition-${student.id}`}
            value={info.medicalCondition || ''}
            onChange={(e) => onChange('medicalCondition', e.target.value)}
            placeholder="Describe any medical conditions the school should be aware of, or leave blank if none"
            rows={3}
          />
        </div>
        )}

        {showOtc && (
          <>
            {showCondition && <Separator />}

            {/* OTC Medication Permission */}
            <OtcMedicationForm
              permission={info.otcMedicationPermission}
              medications={medications}
              onPermissionChange={handleOtcPermissionChange}
              onMedicationToggle={handleMedicationToggle}
              onOtherMedicationChange={handleOtherMedicationChange}
              studentId={student.id}
            />
          </>
        )}

        {showSpecial && (
        <>
        {(showCondition || showOtc) && <Separator />}

        {/* Special Needs / Disabilities */}
        <div className="space-y-2">
          <Label htmlFor={`special-${student.id}`}>
            Special Needs / Disabilities
          </Label>
          <p className="text-sm text-muted-foreground mb-2">
            If your child has a special need or disability, please add any additional information
            you believe we should know in this regard.
          </p>
          <Textarea
            id={`special-${student.id}`}
            value={info.specialNeeds || ''}
            onChange={(e) => onChange('specialNeeds', e.target.value)}
            placeholder="Describe any special needs or disabilities, or leave blank if none"
            rows={3}
          />
        </div>
        </>
        )}

        {showInsurance && (
        <>
        {(showCondition || showOtc || showSpecial) && <Separator />}

        {/* Insurance Information */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Insurance Information</Label>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`insurance-carrier-${student.id}`}>Carrier</Label>
              <Input
                id={`insurance-carrier-${student.id}`}
                value={insuranceInfo.carrier || ''}
                onChange={(e) =>
                  onChange('insuranceInfo', { ...insuranceInfo, carrier: e.target.value })
                }
                placeholder="Insurance carrier"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`insurance-group-${student.id}`}>Group #</Label>
              <Input
                id={`insurance-group-${student.id}`}
                value={insuranceInfo.groupNumber || ''}
                onChange={(e) =>
                  onChange('insuranceInfo', { ...insuranceInfo, groupNumber: e.target.value })
                }
                placeholder="Group number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`insurance-policy-${student.id}`}>Insurance Policy # or ID #</Label>
              <Input
                id={`insurance-policy-${student.id}`}
                value={insuranceInfo.policyNumber || ''}
                onChange={(e) =>
                  onChange('insuranceInfo', { ...insuranceInfo, policyNumber: e.target.value })
                }
                placeholder="Policy number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`insurance-holder-${student.id}`}>Name of Policy Holder or Insured</Label>
              <Input
                id={`insurance-holder-${student.id}`}
                value={insuranceInfo.policyHolderName || ''}
                onChange={(e) =>
                  onChange('insuranceInfo', { ...insuranceInfo, policyHolderName: e.target.value })
                }
                placeholder="Policy holder name"
              />
            </div>
          </div>
        </div>
        </>
        )}
      </CardContent>
    </Card>
  );
}
